import { AdministrationHelper } from "@/Administration/Helpers/AdministrationHelper";
import { Address } from "@/Common/Api/Contacts/Dto/Address";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { Site } from "@/Common/Models/Sites/ResponseModels/Site";
import { CanadaState } from "@/Common/Resources/Lookups/Enums/CommonLookupCodes";

interface ISiteInfoSectionProps {
    site: Site;
}

function SiteInfoSection(props: ISiteInfoSectionProps): JSX.Element {
    const { site } = props;

    const formatAsDaysHoursMinutes = (totalMinutes: number): string => {
        const minutesInDay = 1440;
        const minutesInHour = 60;
        const daysCount = Math.floor(totalMinutes / minutesInDay);
        const hoursCount = Math.floor((totalMinutes - (daysCount * minutesInDay)) / minutesInHour);
        const minutesCount = totalMinutes - (daysCount * minutesInDay) - (hoursCount * minutesInHour);

        const resultElements = [];

        if (daysCount > 0) { resultElements.push(daysCount + " " + cacheStore.GetString("DaysLabel")); }
        if (hoursCount > 0) { resultElements.push(hoursCount + " " + cacheStore.GetString("HoursLabel")); }
        if (minutesCount > 0) { resultElements.push(minutesCount + " " + cacheStore.GetString("MinutesLabel")); }

        if ((totalMinutes === 0) || (totalMinutes === undefined)) {
            resultElements.push(cacheStore.GetString("NoteUnapprovingIsDisabledMessage"));
        }

        return resultElements.join(", ");
    };

    function renderOrganizationInfo(): JSX.Element {
        if (!IsNullOrWhiteSpace(site.ParentSiteId))
            return null;

        return (
            <div>
                <div className="row">
                    <div className="col-lg-4 col-md-2 col-sm-2 text-right">
                        <label className="form-label">{cacheStore.GetString("UnapproveNoteExpiryTimeLabel")}</label>
                    </div>
                    <div className="col-lg-5 col-md-6 col-sm-6 form-field-answer">
                        <strong>{formatAsDaysHoursMinutes(site.UnapproveNoteExpiryInMinutes)}</strong>
                    </div>
                </div>
                <div className="row">
                    <div className="col-lg-4 col-md-2 col-sm-2 text-right" />
                    <div className="col-lg-5 col-md-6 col-sm-6 form-field-answer">
                        {site.IsGMF &&
                            <strong>{cacheStore.GetString("IsGMFLabel")}</strong>
                        }
                        {site.IsGMFU &&
                            <strong>{cacheStore.GetString("UniversityFamilyMedicineGroupAbbrevLabel")}</strong>
                        }
                        {site.IsGMFR &&
                            <strong>{cacheStore.GetString("SuperFamilyMedicineGroupAbbrevLabel")}</strong>
                        }
                    </div>
                </div>
            </div>
        );
    }

    function renderSiteLocationInfo(): JSX.Element {
        if (IsNullOrWhiteSpace(site.ParentSiteId))
            return null;

        const defaultAddress = Address.getDefaultAddress(site.Adresses);

        let result: JSX.Element = null;
        if (Address.isAddressInProvince(defaultAddress, CanadaState.Quebec)) {
            result = (
                <div>
                    <div className="row">
                        <div className="col-lg-4 col-md-2 col-sm-2 text-right">
                            <label className="form-label">{cacheStore.GetString("CodeCELabel")}</label>
                        </div>
                        <div className="col-lg-5 col-md-6 col-sm-6 form-field-answer">
                            <strong>{site.CeLoginRamq}</strong>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-lg-4 col-md-2 col-sm-2 text-right">
                            <label className="form-label">{cacheStore.GetString("AipCodeLabel")}</label>
                        </div>
                        <div className="col-lg-5 col-md-6 col-sm-6 form-field-answer">
                            <strong>{site.AipLogin}</strong>
                        </div>
                    </div>
                </div>
            );
        }

        else if (Address.isAddressInProvince(defaultAddress, CanadaState.BritishColumbia)) {
            const code = AdministrationHelper.mspServiceLocationCodes[site.MspLocationCode];

            result = (
                <div>
                    <div className="row">
                        <div className="col-lg-4 col-md-2 col-sm-2 text-right">
                            <label className="form-label">{cacheStore.GetString("MspLocationCodeLabel")}</label>
                        </div>
                        <div className="col-lg-5 col-md-6 col-sm-6 form-field-answer">
                            {code && <strong>{code.code} - {code.description}</strong>}
                        </div>
                    </div>
                </div>
            );
        }

        if (cacheStore.ClinicSettings.EnablePopulationBasedFunding) {
            result = (
                <>
                    {result}
                    <div>
                        <div className="row">
                            <div className="col-lg-4 col-md-2 col-sm-2 text-right">
                                <label className="form-label">{cacheStore.GetString("MspFacilityNumberLabel")}</label>
                            </div>
                            <div className="col-lg-5 col-md-6 col-sm-6 form-field-answer">
                                <strong>{site.MspFacilityNumber}</strong>
                            </div>
                        </div>
                    </div>
                    <div>
                        <div className="row">
                            <div className="col-lg-4 col-md-2 col-sm-2 text-right">
                                <label className="form-label">{cacheStore.GetString("ClinicPayeeNumberLabel")}</label>
                            </div>
                            <div className="col-lg-5 col-md-6 col-sm-6 form-field-answer">
                                <strong>{site.PayeeNumber}</strong>
                            </div>
                        </div>
                    </div>
                </>
            );
        }

        return result;
    }

    return (
        <div>
            <div className="view-section-header">
                <h3 className="view-section-title">{!IsNullOrWhiteSpace(site.ParentSiteId) ? cacheStore.GetString("SiteInfoLabel") : cacheStore.GetString("OrganizationInfoLabel")}</h3>
            </div>
            <div className="view-section-body">
                <div className="row">
                    <div className="col-lg-4 col-md-2 col-sm-2 text-right">
                        <label className="form-label">{cacheStore.GetString("NameLabel")}</label>
                    </div>
                    <div className="col-lg-5 col-md-6 col-sm-6 form-field-answer">
                        <strong title={site.ClinicalName}>{limitTextLength(site.ClinicalName)}</strong>
                    </div>
                </div>
                <div className="row">
                    <div className="col-lg-4 col-md-2 col-sm-2 text-right">
                        <label className="form-label">{cacheStore.GetString("AcronymLabel")}</label>
                    </div>
                    <div className="col-lg-5 col-md-6 col-sm-6 form-field-answer">
                        <strong>{site.Acronym}</strong>
                    </div>
                </div>
                <div className="row">
                    <div className="col-lg-4 col-md-2 col-sm-2 text-right">
                        <label className="form-label">{cacheStore.GetString("lblSubstitutionCode")}</label>
                    </div>
                    <div className="col-lg-5 col-md-6 col-sm-6 form-field-answer">
                        <strong>{site.ClinicalCode}</strong>
                    </div>
                </div>
                {Address.hasAddressInProvince(site.Adresses, CanadaState.Quebec) && site.ParentSiteId && (
                    <div className="row">
                        <div className="col-lg-4 col-md-2 col-sm-2 text-right" />
                        <div className="col-lg-5 col-md-6 col-sm-6 form-field-answer">
                            <CheckboxControl
                                label={cacheStore.GetString("SelectAsMainEnrollmentSiteForClinicalCodeLabel")}
                                checked={site.MainEnrolmentSiteForClinicalCode}
                                disabled={true}
                            />
                        </div>
                    </div>
                )}
                {renderOrganizationInfo()}
                {renderSiteLocationInfo()}
            </div>
        </div>
    );
}

export default SiteInfoSection;
